/**
 * Utilidades para manejar llamadas a la API
 * Incluye manejo robusto de errores de red y autenticación
 */

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';

/**
 * Obtener la URL base para imágenes y recursos
 * Detecta automáticamente si está en producción o desarrollo
 */
export function getBaseUrl(): string {
  if (process.env.NODE_ENV === 'production') {
    // En producción, usar la URL relativa o detectar desde window.location
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return '';
  }
  // En desarrollo, usar la URL local
  return 'http://hiomenu.local';
}

/**
 * Construir URL completa para imágenes y recursos
 */
export function getImageUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  
  // Si ya es una URL completa, devolverla tal como está
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Si es una ruta relativa, construir la URL completa
  const baseUrl = getBaseUrl();
  const fullUrl = `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
  
  // Logging para depuración en desarrollo
  if (process.env.NODE_ENV === 'development') {
    console.log('[getImageUrl] Path:', path, '-> Full URL:', fullUrl);
  }
  
  return fullUrl;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: boolean;
  message?: string;
  timestamp?: string;
  debug_xml_login?: string; // XML de respuesta del login de Hioffice
  debug_export_response?: string; // Respuesta de la exportación de Hioffice
  loginSuccess?: boolean; // Indica si el login fue exitoso
  exportSuccess?: boolean; // Indica si la exportación fue exitosa
  errorStep?: 'login' | 'export' | 'unknown'; // Paso donde ocurrió el error
  exportError?: string; // Mensaje de error específico de exportación
}

/**
 * Realiza una petición fetch a la API con manejo de errores mejorado
 * Retorna una Response o lanza un error de red
 */
export async function apiFetch(
  route: string,
  options: RequestInit = {},
  token?: string | null
): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // Solo agregar Authorization si hay token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Separar la ruta base de los query parameters
  // Si la ruta contiene '?', separar la ruta base de los query params
  let routeParam = route;
  if (route.includes('?')) {
    const [baseRoute, queryString] = route.split('?');
    // Combinar route con query parameters usando &
    routeParam = `${baseRoute}&${queryString}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}.php?route=${routeParam}`, {
      ...options,
      headers,
    });

    // Si el status es 0 (caso raro pero posible), tratarlo como error de red
    if (response.status === 0) {
      // Crear una respuesta con status 503 (Service Unavailable) para errores de red
      return new Response(
        JSON.stringify({ error: true, message: 'Error de conexión' }),
        { 
          status: 503, 
          statusText: 'Service Unavailable',
          headers: { 'Content-Type': 'application/json', 'X-Network-Error': 'true' }
        }
      );
    }

    return response;
  } catch (error: any) {
    // Si es un error de red (Failed to fetch), crear una respuesta simulada
    if (
      error.message?.includes('Failed to fetch') || 
      error.message?.includes('NetworkError') ||
      error.message === 'NETWORK_ERROR' ||
      error.name === 'TypeError' ||
      error.name === 'NetworkError'
    ) {
      // Crear una respuesta con status 503 (Service Unavailable) para errores de red
      // Agregamos un header personalizado para identificar que es un error de red
      return new Response(
        JSON.stringify({ error: true, message: 'Error de conexión. Verifica tu conexión a internet.' }),
        { 
          status: 503, 
          statusText: 'Service Unavailable',
          headers: { 'Content-Type': 'application/json', 'X-Network-Error': 'true' }
        }
      );
    }
    throw error;
  }
}

/**
 * Parsea la respuesta JSON de manera segura
 * Maneja errores de red y respuestas vacías
 */
export async function parseJsonResponse<T = any>(response: Response): Promise<ApiResponse<T>> {
  // Verificar si es un error de red usando el helper
  if (isNetworkErrorResponse(response)) {
    throw new Error('NETWORK_ERROR');
  }

  try {
    // Si la respuesta está vacía, lanzar error
    const text = await response.text();
    if (!text || text.trim() === '') {
      throw new Error('EMPTY_RESPONSE');
    }

    try {
      const json = JSON.parse(text);
      return json;
    } catch (parseError) {
      // Si no se puede parsear JSON, lanzar error
      throw new Error('INVALID_JSON');
    }
  } catch (error: any) {
    // Si hay un error al leer la respuesta (ej: error de red), lanzar error de red
    if (error.message === 'NETWORK_ERROR' || error.message?.includes('Failed to fetch')) {
      throw new Error('NETWORK_ERROR');
    }
    throw error;
  }
}

/**
 * Verifica si una Response es un error de red
 * Usa el header personalizado X-Network-Error para identificar errores de conexión
 */
export function isNetworkErrorResponse(response: Response): boolean {
  return response.status === 503 && response.headers.get('X-Network-Error') === 'true';
}

/**
 * Verifica si un error es de red (no debe cerrar sesión)
 */
export function isNetworkError(error: any): boolean {
  if (error instanceof Error) {
    return (
      error.message === 'NETWORK_ERROR' ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Network request failed') ||
      error.message.includes('conexión')
    );
  }
  return false;
}

/**
 * Verifica si un error es de autenticación (debe cerrar sesión)
 * SOLO debe cerrar sesión si el status es 401 (Unauthorized)
 * NO debe cerrar sesión por errores 500, errores de BD, etc.
 */
export function isAuthError(status: number, json: any): boolean {
  // Solo cerrar sesión si es realmente un error 401 de autenticación
  if (status === 401) {
    return true;
  }
  
  // También verificar si el mensaje de error indica específicamente un problema de token
  if (json && json.message) {
    const message = String(json.message).toLowerCase();
    if (
      message.includes('token inválido') ||
      message.includes('token expirado') ||
      message.includes('token faltante') ||
      message.includes('unauthorized') ||
      message.includes('no autorizado') ||
      message.includes('no autorizado') ||
      message.includes('credenciales inválidas')
    ) {
      return true;
    }
  }
  
  // NO cerrar sesión por errores genéricos (500, errores de BD, etc.)
  return false;
}

/**
 * Función helper completa para hacer peticiones a la API
 * Maneja automáticamente errores de red y autenticación
 */
export async function apiRequest<T = any>(
  route: string,
  options: RequestInit = {},
  token?: string | null
): Promise<ApiResponse<T>> {
  try {
    const response = await apiFetch(route, options, token);
    const json = await parseJsonResponse<T>(response);

    // Si hay error de autenticación, el código que llama debe manejarlo
    if (isAuthError(response.status, json)) {
      return json;
    }

    return json;
  } catch (error: any) {
    // Si es error de red, devolver un objeto que indique el error pero no cerrar sesión
    if (isNetworkError(error)) {
      return {
        success: false,
        error: true,
        message: 'Error de conexión. Verifica tu conexión a internet.',
      };
    }

    // Otros errores
    throw error;
  }
}